// Producer-side project hooks (list / create / fetch).
//
// `projects.ts` in this folder is owned by the actor surface and only exposes
// actor-scoped views. Producer endpoints live under /api/v1/projects (org RLS)
// and need their own keys / DTOs, so they live here.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type ListEnvelope, api, unwrapItems } from '../api'
import type { ProjectDetail, ProjectStatus, ProjectSummary } from './producer-types'

export const producerProjectsKey = ['producer', 'projects'] as const
export const producerProjectKey = (projectId: string) => ['producer', 'project', projectId] as const

/** Org-scoped project list (RLS handles tenancy server-side). */
export function useProducerProjects() {
  return useQuery({
    queryKey: producerProjectsKey,
    queryFn: () => api<ListEnvelope<ProjectSummary>>('/api/v1/projects').then(unwrapItems),
  })
}

/** Producer view of a single project: episodes + dashboard rollups. */
export function useProducerProject(projectId: string | undefined) {
  return useQuery({
    queryKey: projectId ? producerProjectKey(projectId) : ['producer', 'project', '__none__'],
    enabled: Boolean(projectId),
    queryFn: () => api<ProjectDetail>(`/api/v1/projects/${projectId}`),
  })
}

export interface CreateProjectInput {
  name: string
  /** 3-6 alphanumeric uppercase. Validated client-side; server enforces too. */
  code: string
  /** ISO date (YYYY-MM-DD). */
  due_date: string | null
}

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      api<ProjectSummary>('/api/v1/projects', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: producerProjectsKey })
    },
  })
}

export interface UpdateProjectStatusInput {
  status: ProjectStatus
}

export function useUpdateProjectStatus(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateProjectStatusInput) =>
      api<ProjectSummary>(`/api/v1/projects/${projectId}/status`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: producerProjectKey(projectId) })
      void qc.invalidateQueries({ queryKey: producerProjectsKey })
    },
  })
}

/** Validate a project code client-side. Server uses the same rule. */
export function isValidProjectCode(code: string): boolean {
  return /^[A-Z0-9]{3,6}$/.test(code)
}

// ── Project-level QC thresholds ─────────────────────────────────────────────
// Persisted as a `qc_thresholds` jsonb field on the projects row. The dedicated
// endpoint `/projects/:id/qc-thresholds` is preferred when present; falls back
// to PATCH /projects/:id with the embedded jsonb otherwise.

export interface QcThresholdsInput {
  clipping_threshold_db: number
  noise_floor_max_db: number
  length_variance_pct: number
  misread_strict: boolean
}

/**
 * Save per-project QC thresholds.
 *
 * Tries the planned dedicated endpoint first, then falls back to the generic
 * PATCH /projects/:id with `qc_thresholds` in the body. The PATCH path will
 * 400 today (the schema only accepts name/code/status/due_date) — when the
 * backend lands either route, this hook will start succeeding without a
 * client-side change.
 *
 * TODO(api): once `/projects/:id/qc-thresholds` ships, drop the PATCH fallback.
 */
export function useUpdateProjectQcThresholds(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: QcThresholdsInput) => {
      try {
        return await api<QcThresholdsInput>(`/api/v1/projects/${projectId}/qc-thresholds`, {
          method: 'POST',
          body: JSON.stringify(input),
        })
      } catch (e) {
        // Fall back to the generic project PATCH with embedded jsonb.
        await api<unknown>(`/api/v1/projects/${projectId}`, {
          method: 'PATCH',
          body: JSON.stringify({ qc_thresholds: input }),
        })
        return input
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: producerProjectKey(projectId) })
    },
  })
}
